import { useEffect, useRef, useState } from 'react';

export const enum TimeUnit {
  Now = 'Now',
  Seconds = 'Sec',
  FiveMinutes = 'five',
  Minutes = 'Min',
  Hours = 'Hr',
  Days = 'Day',
  Weeks = 'Week',
  Months = 'Month',
  Years = 'Year',
}

export const oneSecond = 1000; // ms
export const oneMinute = 60 * oneSecond;
export const oneHour = 60 * oneMinute;
export const oneDay = 24 * oneHour;
export const oneWeek = 7 * oneDay;
export const oneMonth = 4 * oneWeek;
export const oneYear = 12 * oneMonth;

const TimeUnitIntervals: [number, TimeUnit, number][] = [
  [oneYear, TimeUnit.Years, 1],
  [oneMonth, TimeUnit.Months, 1],
  [oneWeek, TimeUnit.Weeks, 1],
  [oneDay, TimeUnit.Days, 1],
  [oneHour, TimeUnit.Hours, 1],
  [oneMinute * 5, TimeUnit.Minutes, 5],
  [oneMinute, TimeUnit.Minutes, 1],
  // [oneSecond, TimeUnit.Seconds, 1],
  [oneMinute, TimeUnit.Now, 1],
];

/**
 * In a nutshell using normal setInterval and control the
 * delay time dynamically is quite complex this solves it
 *
 * copied from https://overreacted.io/making-setinterval-declarative-with-react-hooks
 * @param callback
 * @param delay
 */
function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>();
  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      if (savedCallback.current) savedCallback.current();
    }
    if (delay !== null && delay < oneDay) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

/**
 * Calculates the next interval based on diff between current time and specified date
 * @param date
 * @returns [newInterval, elapsed, unit]
 */
function calculateInterval(
  date: string,
  intervals: [number, TimeUnit, number][],
): [number, number, TimeUnit] {
  const timeDiff = new Date().getTime() - new Date(date).getTime();

  let [interval, unit, step] =
    intervals.find(([interval]) => timeDiff >= interval) ||
    intervals.slice(-1)[0]; // sets the initial timer to the last element in config array

  let elapsed = Math.floor(timeDiff / interval) * step;

  return [interval, elapsed, unit];
}

/**
 * Calculates the next interval based on diff between current time and specified date
 * @param date
 * @param intervals config array
 * @returns [elapsed, unit]
 */
export default function useElapsedTimeFromNow(
  date: string,
  intervals: [number, TimeUnit, number][] = TimeUnitIntervals,
): [number?, TimeUnit?] {
  // law gai le bug rabenna ma3ak
  const [elapsed, setElapsed] = useState<number>();
  const [unit, setUnit] = useState<TimeUnit>();
  const [interval, setInterval] = useState<number>(0);

  const calculate = () => {
    let [interval, elapsed, unit] = calculateInterval(date, intervals);
    // elapsed time in unit measure ie {elapsed 5, unit: Min}, {elapsed: 1, unit: Week}, ... etc
    setElapsed(elapsed);
    setUnit(unit);
    setInterval(interval);
  };

  // run the first instance because setInterval do the delay first then runs
  useEffect(calculate, []);
  useInterval(calculate, interval);

  //  listen on date changes otherwise after submitting a new message it will
  //  use the last timer delay and will not display Now message
  useEffect(calculate, [date]);
  return [elapsed, unit];
}
